import Fuse from 'fuse.js';
import { useEffect, useMemo } from 'react';

import { SearchBar } from '@/components/SearchBar/SearchBar';
import {
	useDcrFormSearchTerm,
	useSetDcrFormFilteredFields,
	useSetDcrFormSearchTerm
} from '@/core/store/dcrs/dcrSelectors';

import { EntityType } from '../../../../core/graphql/generated/types';
import { Field, useDcrFields } from '../../_constants/useDcrFields';
import { useDcrContext } from '../../_context/DcrContext';

type Props = {
	entityType: EntityType;
};

export const FormSearch = ({ entityType }: Props) => {
	const { hcpFields, hcoFields } = useDcrFields();
	const { unavailableFields } = useDcrContext();

	const setQuery = useSetDcrFormSearchTerm();
	const query = useDcrFormSearchTerm();
	const setFilteredFields = useSetDcrFormFilteredFields();

	const fuse = useMemo(() => {
		const fields = Object.entries(
			entityType === EntityType.Hcp ? hcpFields : hcoFields
		).reduce(
			(acc, [id, data]) => {
				if (!data.hidden && !unavailableFields?.includes(id)) {
					acc.push({
						id,
						...data
					});
				}

				return acc;
			},
			[] as (Field & {
				id: string;
			})[]
		);

		return new Fuse(fields, { keys: ['label'], threshold: 0.17 });
	}, [entityType, unavailableFields, hcpFields, hcoFields]);

	useEffect(() => {
		if (query !== '') {
			const results = fuse.search(query);
			const filteredFields = results.reduce(
				(acc, { item: { id, section } }) => {
					const sections = Array.isArray(section) ? section : [section];

					sections.forEach(s => {
						if (!acc[s]) {
							acc[s] = [];
						}

						if (!acc[s].includes(id)) {
							acc[s].push(id);
						}
					});

					return acc;
				},
				{} as Record<string, string[]>
			);

			setFilteredFields(filteredFields);
		}
	}, [fuse, query, setFilteredFields]);

	return (
		<SearchBar
			value={query}
			placeholder='Search field'
			handleChange={e => setQuery(e.target.value)}
			handleClear={() => setQuery('')}
			fullWidth
			customSx={{
				root: {
					maxWidth: 'unset'
				}
			}}
		/>
	);
};
